/**
 * 患者仓储实现
 *
 * 实现 IPatientRepository 接口
 *
 * @module patient-management/infrastructure/repositories
 */

import type {
  IPatientRepository,
  PatientQueryParams,
  PagedResult,
} from "./patientRepositoryInterface";
import type {
  Patient,
  PatientSummary,
  CreatePatientParams,
  UpdatePatientParams,
} from "@shared/domain/patientTypes";
import * as patientApi from "../infrastructure/patientApi";
import {
  mapApiToEntity,
  mapCreateParamsToRequest,
  mapUpdateParamsToRequest,
  mapApiListToEntities,
  mapApiListToSummaries,
} from "./patientMapper";
import { cache, createCacheKey } from "@shared/utils/CacheService";
import { createModuleLogger } from "@shared/utils/Logger";

const logger = createModuleLogger("PatientRepository");

/**
 * 患者仓储实现类
 */
export class PatientRepository implements IPatientRepository {
  async getById(id: string): Promise<Patient | null> {
    try {
      // 静默读取：患者主档缺失（已删除 / 历史脏引用）是可容忍的业务结果，由调用方
      // 决定如何降级提示；不应触发全局错误弹窗，否则同一患者在多个面板并发解析时
      // 会重复弹「患者未找到」，并把可降级场景误报成系统故障。
      const response = await patientApi.fetchPatientByIdentity(id, {
        silent: true,
      });
      return mapApiToEntity(response);
    } catch (error) {
      logger.error("获取患者失败:", error);
      return null;
    }
  }

  async getList(
    params?: PatientQueryParams,
  ): Promise<PagedResult<PatientSummary>> {
    try {
      const page = params?.page || 1;
      const pageSize = params?.pageSize || 20;

      const response = await patientApi.fetchPatients({
        keyword: params?.keyword,
        status: params?.status,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: params?.sortBy,
        sort_order: params?.sortOrder,
      });

      const items = mapApiListToSummaries(response);

      // 注意：如果后端返回分页信息，应该使用后端的 total
      // 这里假设后端返回的是当前页的数据
      return {
        items,
        total: items.length, // 实际应从后端获取
        page,
        pageSize,
        totalPages: 1, // 实际应计算
      };
    } catch (error) {
      logger.error("获取患者列表失败:", error);
      return { items: [], total: 0, page: 1, pageSize: 20, totalPages: 0 };
    }
  }

  async getAll(limit: number = 100, skip: number = 0): Promise<Patient[]> {
    try {
      const response = await patientApi.fetchPatients({ limit, skip });
      return mapApiListToEntities(response);
    } catch (error) {
      logger.error("获取所有患者失败:", error);
      return [];
    }
  }

  async search(keyword: string, limit: number = 20): Promise<PatientSummary[]> {
    try {
      const response = await patientApi.searchPatients(keyword, limit);
      return mapApiListToSummaries(response);
    } catch (error) {
      logger.error("搜索患者失败:", error);
      return [];
    }
  }

  async create(params: CreatePatientParams): Promise<Patient> {
    const request = mapCreateParamsToRequest(params);
    const response = await patientApi.createPatient(request);
    return mapApiToEntity(response);
  }

  async update(id: string, params: UpdatePatientParams): Promise<Patient> {
    const request = mapUpdateParamsToRequest(params);
    const response = await patientApi.updatePatient(id, request);
    return mapApiToEntity(response);
  }

  async delete(id: string): Promise<boolean> {
    try {
      const result = await patientApi.deletePatient(id);
      return result.success;
    } catch (error) {
      logger.error("删除患者失败:", error);
      return false;
    }
  }

  async exists(id: string): Promise<boolean> {
    const patient = await this.getById(id);
    return patient !== null;
  }

  async findByPhone(phone: string): Promise<Patient | null> {
    // 使用缓存避免重复API调用
    return cache.get(
      createCacheKey("patient_by_phone", phone),
      async () => {
        try {
          const response = await patientApi.findPatientByPhone(phone);
          return response ? mapApiToEntity(response) : null;
        } catch (error) {
          logger.error("根据手机号查找患者失败:", error);
          return null;
        }
      },
      { ttl: 10 * 60 * 1000 }, // 10分钟缓存
    );
  }

  async findByIdCard(idCard: string): Promise<Patient | null> {
    try {
      const response = await patientApi.findPatientByIdCard(idCard);
      return response ? mapApiToEntity(response) : null;
    } catch (error) {
      logger.error("根据身份证查找患者失败:", error);
      return null;
    }
  }

  async getRecent(limit: number = 10): Promise<PatientSummary[]> {
    try {
      const response = await patientApi.fetchPatients({
        limit,
        sort_by: "updated_at",
        sort_order: "desc",
      });
      return mapApiListToSummaries(response);
    } catch (error) {
      logger.error("获取最近患者失败:", error);
      return [];
    }
  }
}

/**
 * 创建患者仓储实例
 */
export function createPatientRepository(): IPatientRepository {
  return new PatientRepository();
}

/**
 * 默认患者仓储实例
 */
export const patientRepository = new PatientRepository();

/**
 * 同一患者主键的并发查询共享同一个在途请求。
 *
 * 背景：病历面板、AI 工作区、患者管理面板会并发解析同一份病历的患者主档；
 * 历史病历引用的主档可能已不存在，重复请求会放大后端压力与失败次数。
 */
const inFlightPatientLookups = new Map<string, Promise<Patient>>();

/** 业务读取入口：统一经过 patientApi + mapper。 */
export async function getPatientById(id: string): Promise<Patient> {
  const pending = inFlightPatientLookups.get(id);
  if (pending) return pending;

  const request = patientRepository
    .getById(id)
    .then((patient) => {
      if (!patient) throw new Error("患者不存在");
      return patient;
    })
    .finally(() => {
      inFlightPatientLookups.delete(id);
    });

  inFlightPatientLookups.set(id, request);
  return request;
}

/** 业务列表读取入口；旧 patientService 不再承载业务读取。 */
export async function getPatients(limit = 100): Promise<Patient[]> {
  return patientRepository.getAll(limit);
}
